import { useTranslation } from 'react-i18next';
import {
    BodyTextMedium,
    BodyTextSmall,
    ComponentTextSmall,
    HeadingText20,
    HeadingText30,
    HeadingText50,
    pxToRem,
} from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import { colors } from '@modernhealth/vital-foundation';

import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';

import { PathwayFragment } from 'src/modules/pathways/documents/fragments.generated';
import { useFeaturedPathwayProvidersQuery } from 'src/modules/pathways/documents/featuredPathwayProviders.generated';
import { PageContentContainer } from 'src/modules/pathways/components/PageLayout';
import { ProfileCard } from 'src/modules/pathways/components/ProfileCard';
import {
    ImageBottomLeft,
    ImageBottomRight,
} from 'src/modules/pathways/components/PageIllustrations';

interface Props {
    pathway: PathwayFragment;
}

export const PathwayLanding = ({ pathway }: Props) => {
    const { t } = useTranslation();
    const { data: featuredProvidersData } = useFeaturedPathwayProvidersQuery({
        variables: {
            pathwayUuid: pathway.uuid,
        },
    });
    const { metadata } = pathway;

    return (
        <PageContainer>
            <PageContentContainer>
                <HeaderContainer>
                    <ContentContainer>
                        <ComponentTextSmall marginBottom={8}>
                            {t('pathways:pathway')}
                        </ComponentTextSmall>
                        <HeadingText50 marginBottom={16}>{pathway.title}</HeadingText50>
                        <BodyTextMedium marginBottom={36} fontColor="gray85">
                            {pathway.description}
                        </BodyTextMedium>
                        {!!metadata?.landing_page?.what_youll_work_on?.goals && (
                            <>
                                <HeadingText30 marginBottom={24}>
                                    {t('pathways:components.session_modal.goals_title')}
                                </HeadingText30>

                                {metadata?.landing_page?.what_youll_work_on?.goals?.map(goal => {
                                    if (!goal) return null;
                                    return (
                                        <GoalsContainer key={goal.text}>
                                            <img src={goal.icon} height={46} width={46} alt="" />
                                            <BodyTextMedium marginLeft={16} fontColor="gray85">
                                                {goal.text}
                                            </BodyTextMedium>
                                        </GoalsContainer>
                                    );
                                })}
                            </>
                        )}
                        <WhatYoullWorkOnButton
                            rounded
                            icon="arrow-right"
                            iconAlign="end"
                            to={`/pathways/${pathway.uuid}/create`}
                            variant="primary"
                            size="large"
                        >
                            {t('pathways:action.get_started')}
                        </WhatYoullWorkOnButton>
                    </ContentContainer>

                    <Image src={metadata?.landing_page?.what_youll_work_on?.background_image_web} />
                </HeaderContainer>
                {!!metadata?.landing_page?.how_it_works && (
                    <BodyContainer>
                        <HeadingText50 marginTop={50} marginBottom={6}>
                            {metadata?.landing_page?.how_it_works?.title}
                        </HeadingText50>
                        <BodyTextMedium marginBottom={24}>
                            {metadata?.landing_page?.how_it_works?.subtitle}
                        </BodyTextMedium>
                        <HowItWorksContainer>
                            <HowItWorksImageContainer>
                                <HowItWorksImage
                                    src={metadata?.landing_page?.how_it_works?.image}
                                />
                            </HowItWorksImageContainer>
                            <StepsContainer>
                                {metadata?.landing_page?.how_it_works?.steps?.map(step => {
                                    if (!step) return null;
                                    return (
                                        <OrderedItem key={step.title}>
                                            <GoalContentContainer>
                                                <HeadingText20>{step.title}</HeadingText20>
                                                <BodyTextSmall>{step.subtitle}</BodyTextSmall>
                                            </GoalContentContainer>
                                        </OrderedItem>
                                    );
                                })}
                            </StepsContainer>
                        </HowItWorksContainer>
                    </BodyContainer>
                )}

                {(featuredProvidersData?.featuredPathwayProviders ?? []).length > 0 && (
                    <div>
                        <HeadingText50 marginTop={50} marginBottom={6}>
                            {metadata?.landing_page?.specialists?.title}
                        </HeadingText50>
                        <BodyTextMedium marginBottom={24}>
                            {metadata?.landing_page?.specialists?.subtitle}
                        </BodyTextMedium>
                        {featuredProvidersData?.featuredPathwayProviders && (
                            <PathwaySpecialistsContainer>
                                {featuredProvidersData.featuredPathwayProviders.map(featured => {
                                    if (!featured?.provider.firstName) return;
                                    return (
                                        <ProfileCardContainer key={featured.provider.uuid}>
                                            <ProfileCard
                                                backgroundColor="white"
                                                contentBorder
                                                coverImage={
                                                    featured.featuredAvatar ||
                                                    featured.provider.avatar ||
                                                    ''
                                                }
                                                variant="small"
                                            >
                                                <ProfileContentContainer>
                                                    <HeadingText30>{`${featured.provider.firstName} ${featured?.provider.lastName}`}</HeadingText30>
                                                    <BodyTextSmall marginBottom={14}>
                                                        {t('pathways:pathway_specialist')}
                                                    </BodyTextSmall>
                                                    <BodyTextMedium marginBottom={10}>
                                                        {featured.biography ||
                                                            featured.provider.digitalBio}
                                                    </BodyTextMedium>
                                                </ProfileContentContainer>
                                            </ProfileCard>
                                        </ProfileCardContainer>
                                    );
                                })}
                            </PathwaySpecialistsContainer>
                        )}
                    </div>
                )}

                <Footer>
                    <HeadingText50 marginTop={100} marginBottom={32}>
                        {metadata?.landing_page?.get_started?.title}
                    </HeadingText50>
                    <div>
                        <InternalLinkButton
                            rounded
                            icon="arrow-right"
                            iconAlign="end"
                            to={`/pathways/${pathway.uuid}/create`}
                            variant="primary"
                            size="large"
                        >
                            {t('pathways:action.get_started')}
                        </InternalLinkButton>
                    </div>
                </Footer>
            </PageContentContainer>
            <ImageBottomLeft />
            <ImageBottomRight />
        </PageContainer>
    );
};

const PageContainer = styled.div({
    position: 'relative',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
});

const HeaderContainer = styled.div({
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    overflowY: 'clip',
    minHeight: '726px',
});

const BodyContainer = styled.div({
    padding: pxToRem(20),
});

const Image = styled.img({
    objectFit: 'cover',
    width: '100vw',
    minHeight: '726px',
});

const GoalsContainer = styled.div({
    display: 'flex',
    justifyContent: 'flex-start',
    padding: pxToRem(5),
    marginBottom: pxToRem(8),
});

const ContentContainer = styled.div({
    padding: pxToRem(20),
    position: 'absolute',
    left: 0,
    width: '430px',
});

const WhatYoullWorkOnButton = styled(InternalLinkButton)({
    marginTop: '36px',
});

const StepsContainer = styled.ol({
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    counterReset: 'steps',
    listStyle: 'none',
});

const OrderedItem = styled.li({
    display: 'inline-flex',
    counterIncrement: 'steps',
    '::before': {
        content: 'counter(steps)',
        marginRight: '5px',
        marginTop: '2px',
        background: 'transparent',
        color: colors.blue55,
        minWidth: pxToRem(32),
        height: pxToRem(32),
        borderRadius: '50%',
        border: `1px solid ${colors.blue55}`,
        display: 'inline-grid',
        placeItems: 'center',
        lineHeight: pxToRem(1),
    },
});

const GoalContentContainer = styled.div({
    marginLeft: '15px',
});

const HowItWorksImageContainer = styled.div({
    flex: 1,
    width: '80%',
    maxHeight: '450px',
    minHeight: '300px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: pxToRem(30),
    position: 'relative',
});

const HowItWorksContainer = styled.div({
    display: 'flex',
});

const HowItWorksImage = styled.img({
    width: '70%',
    objectFit: 'contain',
    position: 'absolute',
    bottom: 0,
});

const PathwaySpecialistsContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});
const ProfileCardContainer = styled.div({ width: '32%' });

const ProfileContentContainer = styled.div({
    minHeight: '200px',
    padding: pxToRem(16),
});

const Footer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: pxToRem(16),
});
